<template>
  <v-card-text>
    <v-card border>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <b-input
              v-model="incidentCredentials.name"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.name)"
              hide-details
              label="Назва"
              @blur="v$.name.$touch()"></b-input>
          </v-col>

          <v-col cols="12">
            <b-textarea
              v-model="incidentCredentials.description"
              :disabled="!canEditIncident"
              hide-details
              label="Детальний опис"
              rows="5"></b-textarea>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <b-select
              v-model="incidentCredentials.typeId"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.typeId)"
              :items="$directory.get('incidentTypes', incidentSource.type)"
              :loading="$loading.isLoading('incidentTypes')"
              hide-details
              label="Тип інциденту"
              @blur="v$.typeId.$touch()"
              @focus="$directory.fill('incidentTypes')"></b-select>
          </v-col>

          <v-col cols="12" md="6">
            <b-select
              v-model="incidentCredentials.categoryId"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.categoryId)"
              :items="
                $directory.get('incidentCategories', incidentSource.category)
              "
              :loading="$loading.isLoading('incidentCategories')"
              hide-details
              label="Категорія"
              @blur="v$.categoryId.$touch()"
              @focus="$directory.fill('incidentCategories')"></b-select>
          </v-col>

          <v-col cols="12" md="6">
            <b-select
              v-model="incidentCredentials.priorityId"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.priorityId)"
              :items="
                $directory.get('incidentPriorities', incidentSource.priority)
              "
              :loading="$loading.isLoading('incidentPriorities')"
              hide-details
              label="Пріоритет"
              @blur="v$.priorityId.$touch()"
              @focus="$directory.fill('incidentPriorities')">
              <template #selection="{ item }">
                <div class="d-flex align-center ga-2">
                  <v-icon
                    :color="getIncidentPriorityColor(item.raw.id)"
                    size="x-small">
                    mdi-circle
                  </v-icon>
                  <span>{{ item.raw.name }}</span>
                </div>
              </template>
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon
                      :color="getIncidentPriorityColor(item.raw.id)"
                      size="x-small">
                      mdi-circle
                    </v-icon>
                  </template>
                </v-list-item>
              </template>
            </b-select>
          </v-col>

          <v-col cols="12" md="6">
            <b-label label="Підрозділ / Підрозділи інциденту"></b-label>
            <div class="d-flex ga-3 align-center object-block">
              <v-chip
                v-for="department in incidentSource.departments"
                :key="department.id"
                border
                :closable="canDeleteIncidentDepartment"
                color="blue-darken-3"
                density="compact"
                rounded="lg"
                @click:close="deleteIncidentDepartment(department.id)">
                {{
                  department.internalDepartment?.name ||
                  department.internal_department?.name ||
                  department.name ||
                  department.internalDepartmentId
                }}
              </v-chip>
              <span
                v-if="!incidentSource.departments?.length"
                class="text-disabled">
                Не вказано
              </span>
              <v-dialog
                v-if="
                  canCreateIncidentDepartment || canDeleteIncidentDepartment
                "
                v-model="departmentDialog"
                width="400">
                <template #activator="{ props }">
                  <v-btn
                    border="dashed thin"
                    color="blue-darken-3"
                    rounded="lg"
                    size="small"
                    v-bind="props"
                    variant="text">
                    <div
                      class="d-flex align-center ga-1 font-weight-medium text-blue-darken-3">
                      <v-icon color="blue-darken-3" icon="mdi-plus"></v-icon>
                      <span>Додати</span>
                    </div>
                  </v-btn>
                </template>

                <dialog-template
                  dialog-width="480px"
                  hide-action-buttons
                  subtitle="Оберіть один або декілька підрозділів з довідника"
                  title="Підрозділи інциденту"
                  @close="departmentDialog = false">
                  <b-input
                    v-model="departmentSearch"
                    hide-details
                    placeholder="Пошук підрозділу">
                    <template #prepend-inner>
                      <v-icon icon="mdi-magnify" size="small"></v-icon>
                    </template>
                  </b-input>

                  <div
                    class="d-flex flex-column ga-2 overflow-y-auto mt-3"
                    style="max-height: 500px">
                    <v-card
                      v-for="department in filteredDepartments"
                      :key="department.id"
                      :class="{
                        'selected-department': incidentSource.departments.some(
                          d => d.internalDepartmentId === department.id
                        ),
                      }"
                      :disabled="
                        $loading.isLoading([
                          `createIncidentDepartment_${department.id}`,
                          `deleteIncidentDepartment_${department.id}`,
                        ]) ||
                        (incidentSource.departments.some(
                          d => d.internalDepartmentId === department.id
                        )
                          ? !canDeleteIncidentDepartment
                          : !canCreateIncidentDepartment)
                      "
                      :loading="
                        $loading.isLoading([
                          `createIncidentDepartment_${department.id}`,
                          `deleteIncidentDepartment_${department.id}`,
                        ])
                      "
                      border>
                      <v-card-text class="pa-1">
                        <v-checkbox
                          :label="department.name"
                          :model-value="
                            incidentSource.departments.some(
                              d => d.internalDepartmentId === department.id
                            )
                          "
                          class="w-100"
                          color="blue-darken-3"
                          hide-details
                          @update:model-value="
                            $event
                              ? createIncidentDepartment(department.id)
                              : (() => {
                                  const item = incidentSource.departments.find(
                                    d =>
                                      d.internalDepartmentId === department.id
                                  )
                                  if (item) deleteIncidentDepartment(item.id)
                                })()
                          ">
                        </v-checkbox>
                      </v-card-text>
                    </v-card>

                    <div
                      v-if="!filteredDepartments.length"
                      class="text-disabled text-center py-5">
                      Дані відсутні
                    </div>
                  </div>

                  <div class="d-flex justify-end mt-5">
                    <v-btn
                      border
                      color="white"
                      density="comfortable"
                      @click="departmentDialog = false">
                      Готово
                    </v-btn>
                  </div>
                </dialog-template>
              </v-dialog>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <b-input-date
              v-model="incidentCredentials.occurredDate"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.occurredDate)"
              hide-details
              label="Дата виникнення"
              @blur="v$.occurredDate.$touch()"></b-input-date>
          </v-col>

          <v-col cols="12" md="6">
            <b-input-date
              v-model="incidentCredentials.detectedDate"
              :disabled="!canEditIncident"
              :error-messages="getErrorMessages(v$.detectedDate)"
              hide-details
              label="Дата виявлення"
              @blur="v$.detectedDate.$touch()"></b-input-date>
          </v-col>

          <v-col
            v-if="incidentSource.resolution?.resolvedDate"
            cols="12"
            md="6">
            <b-input-date
              :model-value="incidentSource.resolution.resolvedDate"
              disabled
              hide-details
              label="Дата врегулювання"></b-input-date>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        <risk-assessment-block></risk-assessment-block>
        <resolution-block></resolution-block>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text class="bg-grey-lighten-5">
        <v-row>
          <v-col cols="12" md="6">
            <div
              class="text-uppercase font-weight-bold text-medium-emphasis font-size-11">
              Ініціатор
            </div>
            <div>
              {{
                incidentSource.initiator
                  ? getPersonName(incidentSource.initiator, { initials: true })
                  : ''
              }}
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div
              class="text-uppercase font-weight-bold text-medium-emphasis font-size-11">
              Дата створення
            </div>
            <span>{{
              new CustomDate(incidentSource.created_at).toString({
                time: true,
                format: 'locale-hybrid',
              })
            }}</span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-card-text>
</template>

<script lang="ts" setup>
import {
  BInput,
  BInputDate,
  BLabel,
  BSelect,
  BTextarea,
} from 'best-modules/components'
import { useIncident } from '../Incident'
import { getIncidentPriorityColor } from '../../constants'
import DialogTemplate from '@/dialog/DialogTemplate.vue'
import { computed, ref, watch } from 'vue'
import { fillDirectory, getDirectory } from '@/directory'
import { getPersonName } from 'best-modules/utils/helpers'
import { CustomDate, getErrorMessages } from 'best-modules/utils'
import RiskAssessmentBlock from '../components/RiskAssessmentBlock.vue'
import ResolutionBlock from '../components/ResolutionBlock.vue'

defineOptions({
  name: 'IncidentOverview',
})

const {
  canEditIncident,
  canCreateIncidentDepartment,
  canDeleteIncidentDepartment,
  createIncidentDepartment,
  deleteIncidentDepartment,
  incidentCredentials,
  incidentSource,
  v$,
} = useIncident()

const departmentDialog = ref<boolean>(false)
const departmentSearch = ref<string | null>(null)

const filteredDepartments = computed(() => {
  const departments = getDirectory('internalDepartments') || []

  if (!departmentSearch.value) return departments

  return departments.filter(department =>
    department.name
      .toLowerCase()
      .includes(departmentSearch.value!.toLowerCase())
  )
})

watch(
  () => departmentDialog.value,
  val => {
    if (val) {
      fillDirectory('internalDepartments')
    }
  }
)
</script>

<style lang="scss" scoped>
.object-block {
  padding: 3px 13px;
  background: #f5f6fa;
  border-radius: 9px;
  border: 1px solid #f0f0f5;
  color: #1a1a1b;
  min-height: 40px;
  max-height: 40px;

  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
:deep(.v-checkbox .v-selection-control) {
  min-height: 25px !important;
}
.selected-department {
  background: rgba(25, 118, 210, 0.12);
  border: 1px solid rgb(25, 118, 210);

  :deep(.v-checkbox .v-label) {
    color: rgb(25, 118, 210);
    font-weight: 700;
  }
}
</style>
